import React from 'react';
import { RouterProvider } from "react-router-dom";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Head } from "./components/Head";
import { OuterErrorBoundary } from "./components/OuterErrorBoundary";
import { router } from "./router";
import { ThemeProvider } from "./components/ThemeProvider";
import { DEFAULT_THEME } from "./constants/default-theme";
import { Toaster } from "./components/ui/toaster";

// Globaler 401-Handler: nur bei echter Authentifizierungsablauf (401) zum Login umleiten.
// 403 (Berechtigungsfehler) löst keinen Logout aus – der User ist noch eingeloggt.
// Gilt für Queries UND Mutations (Statusänderungen, Archivieren, etc.) – vorher fingen
// nur Queries die 401 ab, eine abgelaufene Session bei einer Mutation zeigte nur einen
// Fehler-Toast statt zum Login umzuleiten.
const handleAuthError = (error: unknown) => {
  if ((error as { status?: number })?.status === 401) {
    queryClient.clear();
    if (!window.location.pathname.startsWith('/login')) {
      router.navigate('/login', { replace: true });
    }
  }
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleAuthError }),
  mutationCache: new MutationCache({ onError: handleAuthError }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false, // Prevent unnecessary refetches on tab focus
    },
  },
});

export const AppWrapper = () => {
  return (
    <OuterErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme={DEFAULT_THEME}>
          <RouterProvider router={router} />
          <Head />
          <Toaster />
        </ThemeProvider>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </OuterErrorBoundary>
  );
};